import { useEffect, useRef, useState } from "react";
import Home from "../components/Home";
import Play from "../components/Play";
import Result from "../components/Result";
import Api from "../api/api";
import { decrypt } from "../helpers/functions";
import useQuizStore from "../store/quiz";
import toast, { Toaster } from "react-hot-toast";

const Page = () => {
  const inputsRef = useRef([]);
  const [id, setId] = useState(null);
  const [quiz, setQuiz] = useState();
  const [isModified, setIsModified] = useState(0);
  const [states, setStates] = useState({ home: true, play: false, result: false });
  const [layout] = useState({
    default: ["Q W E R T Y U I O P", "A S D F G H J K L", "Z X C V B N M {bksp}"],
  });

  const {
    getClue,
    setClue,
    getScore,
    setScore,
    values,
    setValues,
    getAttempt,
    setAttempt,
    quizzes,
    setQuizzes,
    getFocusedIndex,
    setFocusedIndex,
    getHighlightedQuestion,
    setHighlightedQuestion,
    getCompleted,
    setCompleted,
    incrementHelp,
    getHelp,
    getStreak,
    getHighlighted,
    setHighlighted,
  } = useQuizStore();

  const handleFocus = index => setFocusedIndex(id, index);
  const handleClick = index => setFocusedIndex(id, index);

  const handleStates = key => {
    setStates(prev => {
      const row = Object.keys(prev).reduce((acc, cur) => {
        acc[cur] = false;
        return acc;
      }, {});

      return { ...row, [key]: true };
    });
  };

  const handleKeyDown = e => {
    const focusedIndex = getFocusedIndex(id);

    const key = typeof e === "string" ? e : e.key;
    if (/^[a-z]$/.test(key?.toLowerCase())) {
      const newValues = [...values];
      newValues[focusedIndex] = key.toUpperCase();
      setValues(newValues);

      let nextIndex = focusedIndex + 1;
      while (nextIndex < newValues.length && values[nextIndex] === null) {
        nextIndex++;
      }
      if (nextIndex < newValues.length) {
        setFocusedIndex(id, nextIndex);
      }
    } else if (["Backspace", "{bksp}"].includes(key)) {
      const newValues = [...values];
      if (newValues[focusedIndex]) {
        newValues[focusedIndex] = "";
      } else {
        let prevIndex = focusedIndex - 1;
        while (prevIndex >= 0 && values[prevIndex] === null) {
          prevIndex--;
        }
        if (prevIndex >= 0) {
          newValues[prevIndex] = "";
          setFocusedIndex(id, prevIndex);
        }
      }
      setValues(newValues);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [values, getFocusedIndex, id]);

  async function getTodayQuiz() {
    const res = await Api.todayQuiz();
    if (res.status === 200) {
      const data = await decrypt(res?.data?.data);
      setQuiz(data);
      setId(data?._id);

      if (!quizzes.find(itm => itm.id === data?._id)) {
        setQuizzes(data?._id);
        const initialValues =
          data?.answer?.split("")?.map(char => (char === " " ? null : "")) || [];
        setValues(initialValues);
      }
    }
  }

  function convertToComparableForm(input) {
    if (Array.isArray(input)) {
      input = input.join("");
    }

    return input.toLowerCase().replace(/\s+/g, "").replace(/,/g, "");
  }

  function adjustScoreAndAttempt(type) {
    const score = getScore(id);
    const attempt = getAttempt(id);
    if (score - 1 <= 0) {
      setScore(id, score - 1);
      setAttempt(id, attempt + 1);
      autoShow();
      return;
    }
    switch (type) {
      case "reveal": {
        setScore(id, score - 1);
        setAttempt(id, attempt + 1);
        incrementHelp(id);
        autoShow();
        checkHelp();
        break;
      }
      case "clue": {
        setScore(id, score - 1);
        setAttempt(id, attempt + 1);
        incrementHelp(id);
        checkHelp();
        autoShow();
        break;
      }
      default: {
        setScore(id, score - 1);
        setAttempt(id, attempt + 1);
        autoShow();

        break;
      }
    }
  }

  // check if help is greater or equal to answer length [for small answers]
  const checkHelp = () => {
    const originalAnswerLength = convertToComparableForm(quiz?.answer)?.length;
    const help = parseInt(getHelp(id), 10);

    console.log(`Help: ${help}, Original Answer Length: ${originalAnswerLength}`);
    if (!isNaN(help) && help >= originalAnswerLength) {
      const answer = quiz?.answer?.split("")?.map(char => (char === " " ? null : char));
      setScore(id, 0);
      setCompleted(id);
      setValues(answer);
      handleStates("result");
    }
  };

  const autoShow = () => {
    if (getScore(id) === 0) {
      const answer = quiz?.answer?.split("")?.map(char => (char === " " ? null : char));
      setCompleted(id);
      setValues(answer);
      handleStates("result");
      return;
    }
  };

  async function handleCheckAnswer() {
    const inputAnswer = convertToComparableForm(values);
    const originalAnswer = convertToComparableForm(quiz?.answer);
    const score = getScore(id);

    if (score > 0) {
      // Compare the answers
      if (inputAnswer === originalAnswer) {
        setCompleted(id);
        handleStates("result");
      } else {
        toast.error("Incorrect Answer");
        adjustScoreAndAttempt("incorrect");
      }
    }
  }

  const revealLetter = () => {
    const unrevealedPositions = values
      .map((value, index) => (value === "" && quiz?.answer[index] ? index : null))
      .filter(index => index !== null);

    if (unrevealedPositions.length > 0) {
      const randomIndex =
        unrevealedPositions[Math.floor(Math.random() * unrevealedPositions.length)];
      const answerLetter = quiz?.answer[randomIndex];

      if (answerLetter) {
        const newValues = [...values];
        newValues[randomIndex] = answerLetter.toUpperCase();
        setValues(newValues);
        adjustScoreAndAttempt("reveal");
      }
    }
  };

  useEffect(() => {
    getTodayQuiz();
  }, []);

  useEffect(() => {
    if (quiz?.question && getHighlighted(id)) {
      const clueWords = getHighlighted(id).split(" ");
      const questionWords = quiz.question.split(" ");

      const highlightedText = questionWords
        .map(word =>
          clueWords.includes(word)
            ? `<span class="text-[#aa00fd] font-medium">${word}</span>`
            : word,
        )
        .join(" ");

      setHighlightedQuestion(id, highlightedText);
    }
  }, [quiz, id, isModified]);

  return (
    <>
      {states.home ? (
        <Home
          streak={getStreak() || 0}
          handlePlay={() => (!getCompleted(id) ? handleStates("play") : handleStates("result"))}
        />
      ) : states.play ? (
        <Play
          id={id}
          quiz={quiz}
          values={values}
          layout={layout}
          clue={getClue(id)}
          setClue={setClue}
          setHighlighted={setHighlighted}
          score={getScore(id)}
          inputsRef={inputsRef}
          setIsModified={setIsModified}
          handleFocus={handleFocus}
          getFocusIndex={getFocusedIndex}
          handleClick={handleClick}
          handleKeyDown={handleKeyDown}
          handleCheckAnswer={handleCheckAnswer}
          adjustScoreAndAttempt={adjustScoreAndAttempt}
          revealLetter={revealLetter}
          highlightedQuestion={getHighlightedQuestion(id)}
          getHelpFn={getHelp}
        />
      ) : states.result ? (
        <Result
          video={quiz?.video}
          score={getScore(id)}
          values={values}
          question={quiz?.question}
        />
      ) : (
        ""
      )}
      <Toaster
        position="top-center"
        reverseOrder={false}
      />
    </>
  );
};

export default Page;
